import { ChangeEvent, Dispatch, useState, KeyboardEvent, useMemo, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';

import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { FilterCheckbox } from 'UIPackage/MainModule/components/FilterCheckbox';
import { SelectInput } from 'UIPackage/MainModule/components/SelectInput';
import { TextInput } from 'UIPackage/MainModule/components/TextInput';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IKnoLevelErvk } from 'APIPackage/CatalogsModule/interfaces/IKnoLevelErvk';

import {
  userInfoSetOrganisationsSearchQuery,
  userInfoGetOrganisations,
  userInfoSetOrganisationsAdminLevelCode,
  userInfoSetOrganisationsParentId,
  userInfoSetOrganisationsSearchCriteria,
} from '../actions/userInfoActions';
import styles from './AddOrganisationsInputs.module.scss';
import {
  getUserInfoOrganisationsAdminLevelCode,
  getUserInfoOrganisationsSearchQuery,
} from '../selectors/userInfoSelectors';
import { getCatalogKnoAdminLevelsSelectOptions } from '../../../CorePackage/CatalogsModule/selectors/catalogsSelectors';

const SEARCH_INPUT_ERROR = 'Поле поиска должно содержать не менее 4 символов';

interface IAddOrganisationsInputsStoreProps {
  knoOrganizationAdminLevelsSelectOptions: IDictionarySelectOption<IKnoLevelErvk>[];
  searchQuery?: string;
  adminLevelCode?: string;
}

const AddOrganisationsInputsWithoutStore = ({
  knoOrganizationAdminLevelsSelectOptions,
  searchQuery,
  adminLevelCode,
}: IAddOrganisationsInputsStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchInputError, setSearchInputError] = useState('');
  const currentAdminLevelSelectOption = useMemo(
    () =>
      adminLevelCode
        ? knoOrganizationAdminLevelsSelectOptions.find(item => item.code === adminLevelCode)
        : knoOrganizationAdminLevelsSelectOptions[0],
    [adminLevelCode, knoOrganizationAdminLevelsSelectOptions],
  );

  const handleSearchInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setSearchInputValue(e.target.value),
    [],
  );

  const handleTextInputKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        if (searchInputValue.length === 0 || searchInputValue.length > 3) {
          setSearchInputError('');
          dispatch(userInfoSetOrganisationsSearchQuery(searchInputValue));
          dispatch(userInfoGetOrganisations());
        } else {
          setSearchInputError(SEARCH_INPUT_ERROR);
        }
      }
    },
    [dispatch, searchInputValue],
  );

  const handleTextInputBlur = useCallback(() => {
    if (searchInputValue.length === 0 || searchInputValue.length > 3) {
      if (searchQuery !== searchInputValue) {
        setSearchInputError('');
        dispatch(userInfoSetOrganisationsSearchQuery(searchInputValue));
        dispatch(userInfoGetOrganisations());
      }
    } else {
      setSearchInputError(SEARCH_INPUT_ERROR);
    }
  }, [dispatch, searchInputValue, searchQuery]);

  const handleAdminLevelsSelectChange = useCallback(
    (value: IDictionarySelectOption<IKnoLevelErvk>) => {
      if (adminLevelCode !== value.code) {
        dispatch(userInfoSetOrganisationsAdminLevelCode(value.code));
        dispatch(userInfoSetOrganisationsParentId());
        dispatch(userInfoGetOrganisations());
      }
    },
    [adminLevelCode, dispatch],
  );

  const handleOgrnCheckboxChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      dispatch(userInfoSetOrganisationsSearchCriteria(e.target.checked));
      dispatch(userInfoGetOrganisations());
    },
    [dispatch],
  );

  return (
    <>
      <TextInput
        className={styles.SearchInput}
        id="keyWord"
        value={searchInputValue}
        placeholder="Ключевое слово"
        error={searchInputError}
        onChange={handleSearchInputChange}
        onKeyDown={handleTextInputKeyDown}
        onBlur={handleTextInputBlur}
      />
      <div className={styles.Group}>
        <div className={styles.Select}>
          <div className={styles.SelectLabel}>Уровень органа государственной власти</div>
          <SelectInput
            className={styles.SelectInput}
            id="adminLevelsSelect"
            name="adminLevelsSelect"
            size="medium"
            isClearable={false}
            options={knoOrganizationAdminLevelsSelectOptions}
            value={currentAdminLevelSelectOption}
            onChange={handleAdminLevelsSelectChange}
          />
        </div>
        <div className={styles.OgrnCheckbox}>
          <FilterCheckbox id="ogrnCheckbox" name="ogrnCheckbox" variant="minimal" onChange={handleOgrnCheckboxChange} />
          <label className={styles.OgrnCheckboxLabel}>Поиск по ОГРН</label>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state: IRootState) => ({
  searchQuery: getUserInfoOrganisationsSearchQuery(state),
  knoOrganizationAdminLevelsSelectOptions: getCatalogKnoAdminLevelsSelectOptions(state),
  adminLevelCode: getUserInfoOrganisationsAdminLevelCode(state),
});

export const AddOrganisationsInputs = connect(mapStateToProps)(AddOrganisationsInputsWithoutStore);
